import { useState } from "react";
import { Loader2, Save, Users } from "lucide-react";
import { emailApi, type DraftInput, type EmailCohort } from "./api";
import { Notice, btnPrimary, inputCls, looksLikeEmail, parseEmails } from "./shared";

type Props = {
  initial?: Partial<DraftInput>;
  cohorts: EmailCohort[];
  messageId?: number;
  version?: number;
  onSaved: (id: number) => void;
  onCancel?: () => void;
  title?: string;
};

export default function DraftForm({ initial, cohorts, messageId, version, onSaved, onCancel, title }: Props) {
  const [subject, setSubject] = useState(initial?.subject || "");
  const [bodyText, setBodyText] = useState(initial?.bodyText || "");
  const [toRaw, setToRaw] = useState((initial?.to || []).join(", "));
  const [cohortId, setCohortId] = useState(initial?.cohortId ? String(initial.cohortId) : "");
  const [request, setRequest] = useState(initial?.request || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const to = parseEmails(toRaw);
  const invalid = to.filter((e) => !looksLikeEmail(e));
  const hasAudience = to.length > 0 || !!cohortId;

  const save = async () => {
    setError(null);
    if (!subject.trim() || !bodyText.trim()) { setError("Add a subject and a body before saving."); return; }
    if (!hasAudience) { setError("Add at least one recipient or choose a cohort."); return; }
    if (invalid.length) { setError(`These addresses look wrong: ${invalid.join(", ")}`); return; }
    const payload: DraftInput & { version?: number } = { subject: subject.trim(), bodyText, to };
    if (cohortId) payload.cohortId = Number(cohortId);
    if (request.trim()) payload.request = request.trim();
    setBusy(true);
    try {
      if (messageId) {
        await emailApi(`/messages/${messageId}`, { method: "PATCH", body: JSON.stringify({ ...payload, version }) });
        onSaved(messageId);
      } else {
        const r = await emailApi<{ id: number; version: number }>("/drafts", { method: "POST", body: JSON.stringify(payload) });
        onSaved(r.id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the draft.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="form-draft">
      {title && <h3 className="font-serif text-xl tracking-tight">{title}</h3>}
      {messageId && <Notice tone="warn">Saving changes resets any approval. The draft must be reviewed and approved again. Edits are blocked once any send has started.</Notice>}
      <label className="block space-y-1.5">
        <span className="text-xs font-semibold text-muted-foreground">Subject</span>
        <input data-testid="input-subject" className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="A short, specific subject line" />
      </label>
      <label className="block space-y-1.5">
        <span className="text-xs font-semibold text-muted-foreground">Message (plain text)</span>
        <textarea data-testid="input-body" className={`${inputCls} min-h-[220px] leading-relaxed font-[inherit]`} value={bodyText} onChange={(e) => setBodyText(e.target.value)} placeholder="Write the email exactly as recipients should read it." />
      </label>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1.4fr_1fr]">
        <label className="block space-y-1.5">
          <span className="text-xs font-semibold text-muted-foreground">Recipients</span>
          <textarea data-testid="input-recipients" className={`${inputCls} min-h-[84px] font-mono text-xs`} value={toRaw} onChange={(e) => setToRaw(e.target.value)} placeholder="name@example.com, another@example.com" />
          <span className="text-[11px] text-muted-foreground">{to.length} address{to.length === 1 ? "" : "es"}{invalid.length ? `, ${invalid.length} invalid` : ""}. Separate with commas or new lines.</span>
        </label>
        <label className="block space-y-1.5">
          <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Add a cohort (optional)</span>
          <select data-testid="select-cohort" className={inputCls} value={cohortId} onChange={(e) => setCohortId(e.target.value)}>
            <option value="">No cohort</option>
            {cohorts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <span className="text-[11px] text-muted-foreground">Active members are resolved on the server and merged with the list, without duplicates.</span>
        </label>
      </div>
      <label className="block space-y-1.5">
        <span className="text-xs font-semibold text-muted-foreground">Why this email (internal note)</span>
        <input data-testid="input-request" className={inputCls} value={request} onChange={(e) => setRequest(e.target.value)} placeholder="e.g. Reminder for the May cohort call" />
      </label>
      {error && <Notice tone="error">{error}</Notice>}
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="button-save-draft" onClick={save} disabled={busy} className={btnPrimary}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {messageId ? "Save changes" : "Save draft"}
        </button>
        {onCancel && <button data-testid="button-cancel-draft" onClick={onCancel} className="text-sm text-muted-foreground hover:text-foreground px-2">Discard</button>}
        <span className="text-xs text-muted-foreground">Saving never sends. Every draft goes to Approvals first.</span>
      </div>
    </div>
  );
}
